import { api, API_BASE } from './apiClient';
import type { StreamRef, TrackEntry, PreflightResult, SourceName } from '@/types/streaming';

function withSource(url: string, source?: SourceName): string {
  if (!source) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}source=${encodeURIComponent(source)}`;
}

export function buildPlaylistUrl(ref: StreamRef, source?: SourceName, lobbyId?: string): string {
  // Must be absolute. HLS.js calls hls.loadSource(thisUrl) directly — no
  // apiClient indirection — and resolves relative URLs against the page
  // origin (cobstreams.com), which the Cloudflare Pages SPA fallback then
  // catches and serves index.html for. The backend lives at API_BASE
  // (api.cobstreams.com), so we prefix here and let HLS.js resolve the
  // proxy URLs inside the playlist body against this absolute URL.
  //
  // `lobbyId`, when watching together, opts this request into the lobby's
  // SHARED backend session — the backend (after validating membership) hands
  // every member an identical, deterministic streamKey so segment URLs match,
  // enabling Cloudflare edge caching + a common P2P swarm. Omitted for solo.
  const path = ref.type === 'movie'
    ? `/api/stream/movie/${ref.tmdbId}/playlist.m3u8`
    : ref.type === 'tv'
    ? `/api/stream/tv/${ref.tmdbId}/${ref.season}/${ref.episode}/playlist.m3u8`
    : `/api/stream/anime/${ref.malId}/${ref.episode}/${ref.audio}/playlist.m3u8`;
  let url = withSource(`${API_BASE}${path}`, source);
  if (lobbyId) {
    const sep = url.includes('?') ? '&' : '?';
    url = `${url}${sep}lobbyId=${encodeURIComponent(lobbyId)}`;
  }
  return url;
}

function tracksUrl(ref: StreamRef, source?: SourceName): string {
  const base = ref.type === 'movie'
    ? `/api/stream/movie/${ref.tmdbId}/tracks.json`
    : ref.type === 'tv'
    ? `/api/stream/tv/${ref.tmdbId}/${ref.season}/${ref.episode}/tracks.json`
    : `/api/stream/anime/${ref.malId}/${ref.episode}/${ref.audio}/tracks.json`;
  return withSource(base, source);
}

export async function getTracks(ref: StreamRef, source?: SourceName): Promise<TrackEntry[]> {
  const res = await api<{ tracks: TrackEntry[] }>(tracksUrl(ref, source), { auth: true });
  // Track URLs land in <track src>, which resolves relative URLs against the
  // page origin — where the SPA fallback serves index.html and the VTT parser
  // silently yields zero cues. Same trap as buildPlaylistUrl: absolutize the
  // backend's proxy paths against API_BASE.
  return res.tracks.map((t) => (t.url.startsWith('/') ? { ...t, url: `${API_BASE}${t.url}` } : t));
}

function preflightUrl(ref: StreamRef, source: SourceName): string {
  const base = ref.type === 'movie'
    ? `/api/stream/movie/${ref.tmdbId}/preflight`
    : ref.type === 'tv'
    ? `/api/stream/tv/${ref.tmdbId}/${ref.season}/${ref.episode}/preflight`
    : `/api/stream/anime/${ref.malId}/${ref.episode}/${ref.audio}/preflight`;
  return `${base}?source=${encodeURIComponent(source)}`;
}

export async function preflightSource(ref: StreamRef, source: SourceName): Promise<PreflightResult> {
  return api<PreflightResult>(preflightUrl(ref, source), { auth: true });
}

function prefetchPath(ref: StreamRef, source?: SourceName): string {
  const base = ref.type === 'movie'
    ? `/api/stream/movie/${ref.tmdbId}/prefetch`
    : ref.type === 'tv'
    ? `/api/stream/tv/${ref.tmdbId}/${ref.season}/${ref.episode}/prefetch`
    : `/api/stream/anime/${ref.malId}/${ref.episode}/${ref.audio}/prefetch`;
  return withSource(base, source);
}

/**
 * Warm the backend resolve cache for a stream. Fire-and-forget on poster
 * hover: by the time the user clicks the backend has already gone through
 * the provider chain so the click only pays for the playlist GET.
 *
 * Best-effort by design — backend returns 204 even on provider failure so
 * we never surface a hover error to the user. The real click path returns
 * proper status codes if the stream is actually broken.
 */
export async function prefetchStream(
  ref: StreamRef,
  source?: SourceName,
  signal?: AbortSignal,
): Promise<void> {
  try {
    await api<void>(prefetchPath(ref, source), { auth: true, signal });
  } catch {
    /* swallow — hover prefetch must never surface as a UI error */
  }
}
